import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Event as EventIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useGetEventsByMallQuery } from "../../features/events/eventsApiSlice";
import { useGetMallByIdQuery } from "../../features/malls/mallsApiSlice";
import { Event } from "../../types";
import {
  EventCard,
  EventsHero,
  EventsSearchBar,
  EventsSearchBarSkeleton,
  EventsSkeleton,
} from "../../components/Events";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import EmptyState from "../../components/Offers/EmptyState";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { PAGINATION } from "../../config/constants";

const Events: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mallId: selectedMallId } = useParams<{ mallId: string }>();

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedEvents, setAllLoadedEvents] = useState<Event[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Get mall from navigation state (passed from MallHub) or fetch as fallback
  const mallFromState = location.state?.mall;
  const { data: mallFromApi } = useGetMallByIdQuery(selectedMallId || "", {
    skip: !selectedMallId || !!mallFromState, // Skip API call if we have mall from state
  });
  const selectedMall = mallFromState || mallFromApi;

  // Fetch events for selected mall
  const {
    data: eventsResponse,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    error: eventsError,
  } = useGetEventsByMallQuery(
    {
      mallId: selectedMallId || "",
      page: currentPage,
      limit: PAGINATION.PAGE_LIMIT,
      searchQuery: debouncedSearchQuery,
    },
    {
      skip: !selectedMallId,
    }
  );

  // Reset page and accumulated data when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllLoadedEvents([]);
  }, [selectedMallId, debouncedSearchQuery]);

  // Accumulate events as pages load
  useEffect(() => {
    if (eventsResponse?.events !== undefined) {
      setAllLoadedEvents((prev) => {
        if (currentPage === 1) {
          return eventsResponse.events;
        }
        // Append new events, avoiding duplicates
        const newEvents = eventsResponse.events.filter(
          (event) => !prev.some((e) => e.id === event.id)
        );
        return [...prev, ...newEvents];
      });
    }
  }, [eventsResponse, currentPage]);

  // Use accumulated events directly (filtering done by API)
  const events = allLoadedEvents;

  const hasMore =
    eventsResponse?.pagination &&
    eventsResponse.pagination.page < eventsResponse.pagination.totalPages;

  // Load next page callback (memoized to prevent infinite loop)
  const loadMore = useCallback(() => {
    if (hasMore && !eventsFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, eventsFetching]);

  // Infinite scroll hook
  useInfiniteScroll(loadMore, !!hasMore, eventsFetching);

  const isLoading = selectedMallId && eventsLoading;

  // Smooth scroll effect - content slides up over hero
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        const maxOffset = 350;
        const offset = Math.min(currentScrollY, maxOffset);
        contentRef.current.style.transform = `translateY(-${offset}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
        pb: 0,
      }}
    >
      {/* Hero Section */}
      <EventsHero selectedMall={selectedMall} totalEvents={events.length} />

      {/* Main Content */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 1100,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 16, md: 24 },
          borderTopRightRadius: { xs: 16, md: 24 },
          boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          willChange: "transform",
          mt: -3,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 3, md: 4 } }}
        >
          {/* Search Bar */}
          {selectedMallId && isLoading ? (
            <EventsSearchBarSkeleton />
          ) : (
            selectedMallId && (
              <EventsSearchBar
                showSearchBar={true}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
              />
            )
          )}

          {/* Events Grid */}
          {!selectedMallId ? (
            <EmptyState
              icon={
                <EventIcon sx={{ fontSize: 80, color: "text.secondary" }} />
              }
              title="No Mall Selected"
              description="Please select a mall from the Explore page to view events"
              action={
                <Box
                  sx={{
                    mt: 3,
                    px: 3,
                    py: 1.5,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "primary.dark",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => navigate("/explore")}
                >
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Go to Explore
                  </Typography>
                </Box>
              }
            />
          ) : isLoading ? (
            <EventsSkeleton />
          ) : eventsError ? (
            <EmptyState
              icon={<EventIcon sx={{ fontSize: 80, color: "error.main" }} />}
              title="Failed to Load Events"
              description="Unable to fetch events. Please try again later."
            />
          ) : events.length === 0 ? (
            <EmptyState
              icon={
                <EventIcon sx={{ fontSize: 80, color: "text.secondary" }} />
              }
              title={searchQuery ? "No Events Found" : "No Events Available"}
              description={
                searchQuery
                  ? "Try adjusting your search query"
                  : "There are currently no events at this mall"
              }
            />
          ) : (
            <Box>
              <AnimatePresence mode="wait">
                <Grid container spacing={3}>
                  {events.map((event: Event, index: number) => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                      >
                        <EventCard event={event} />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </AnimatePresence>

              {/* Loading more indicator */}
              {eventsFetching && currentPage > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 4,
                    mb: 2,
                  }}
                >
                  <CircularProgress size={30} sx={{ mr: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more events...
                  </Typography>
                </Box>
              )}

              {/* End of results message */}
              {!hasMore && events.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 4,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    You've reached the end of the events
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Container>
      </Box>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default Events;
